import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useLingui } from '@lingui/react';

import { formatDocumentTitle, routeTitle } from '@/lib/routeTitles';

/**
 * useDocumentTitle puts a record name in front of the route title while the
 * calling page is mounted: "Mia Muster · Children · Daycare Butler". Detail
 * pages call it with the loaded name; a blank name leaves the route title the
 * shell's announcer set. The previous title comes back on unmount.
 *
 * The effect re-runs on the same path and locale changes as the announcer, and
 * runs after it in the same commit, so the record name is never overwritten.
 */
export function useDocumentTitle(name?: string | null) {
  const { pathname } = useLocation();
  const { i18n } = useLingui();
  const trimmed = name?.trim() ?? '';

  useEffect(() => {
    if (!trimmed) return;
    const previous = document.title;
    document.title = formatDocumentTitle(trimmed, routeTitle(pathname, i18n));
    return () => {
      document.title = previous;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n is a stable instance; the locale drives re-translation.
  }, [trimmed, pathname, i18n.locale]);
}
